// archiveHandlers/karat307Handler.js
import axios from 'axios';
import {
    API_URL, formatValue, getDatePlusOne, switchConvert
} from '../archiveUtils';

// Для Карат-307 НС = -1
const NS_VALUE_K307 = -1;

// ============================================
// Суффиксы имён параметров по типу архива
// ============================================
const SUFFIX = {
    hour: '',
    day: '',
    month: '',
    integral: '',
};

// ============================================
// Форматирование даты из полей date + time
// Карат-307: date="13.07.2017" time="23:59:00" → "13.07.2017 24"
//            date="13.07.2017" time="00:00:00" → "12.07.2017 24"
//            date="13.07.2017" time="05:00:00" → "13.07.2017 05"
// ============================================
const formatDateTimeK307 = (record) => {
    if (!record) return '—';
    const date = record.date || '';
    const time = record.time || '';
    if (!date) return '—';

    // Парсим дату
    let d;
    if (date.includes('.')) {
        const parts = date.split('.');
        const timeParts = time ? time.split(':') : ['0', '0', '0'];
        d = new Date(
            parseInt(parts[2]),
            parseInt(parts[1]) - 1,
            parseInt(parts[0]),
            parseInt(timeParts[0] || 0),
            parseInt(timeParts[1] || 0),
            parseInt(timeParts[2] || 0)
        );
    } else {
        d = new Date(`${date}T${time || '00:00:00'}`);
    }

    if (isNaN(d.getTime())) return `${date} ${time}`.trim() || '—';

    const hours = d.getHours();
    const minutes = d.getMinutes();

    /*
    // 23:59:00 → конец суток, показываем текущую дату + " 24"
    if (hours === 23 && minutes === 59) {
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        return `${day}.${month}.${year} 24`;
    }
    */

    // 00:00:00 → показываем предыдущий день + " 24"
    if (hours === 0 && minutes === 0) {
        const prevDay = new Date(d);
        prevDay.setDate(prevDay.getDate() - 1);
        const day = String(prevDay.getDate()).padStart(2, '0');
        const month = String(prevDay.getMonth() + 1).padStart(2, '0');
        const year = prevDay.getFullYear();
        return `${day}.${month}.${year} 24`;
    }

    // Обычный час
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    const hoursStr = String(hours).padStart(2, '0');
    return `${day}.${month}.${year} ${hoursStr}`;
};

// ============================================
// Построение колонок для часового/суточного/месячного архива
// ============================================
const buildArchiveColumns = (sampleValues, archiveType) => {
    if (!sampleValues) return [];

    const suffix = SUFFIX[archiveType] || '';
    const columns = [];
    columns.push({ key: 'datetime', name: 'Дата/Время', type: 'datetime' });

    // ✅ Время: Наработка + Tmin, Tmax, Tdt, Tf, Tep
    const timeParams = [
        { key: 'Narabotka', name: 'Наработка' },
        { key: 'Tmin', name: 'Tmin' },
        { key: 'Tmax', name: 'Tmax' },
        { key: 'Tdt', name: 'Tdt' },
        { key: 'Tf', name: 'Tf' },
        { key: 'Tep', name: 'Tep' },
    ];
    timeParams.forEach((p) => {
        if (sampleValues[p.key] !== undefined && sampleValues[p.key] !== null) {
            columns.push({ key: p.key, name: `${p.name}${suffix}, час`, unit: 'час', type: 'value', category: 'time' });
        }
    });

    // Температура
    const tempParams = [
        { key: 'T1', name: 'T1' },
        { key: 'T2', name: 'T2' },
        { key: 'Td1', name: 'Td1' },
    ];
    tempParams.forEach((p) => {
        if (sampleValues[p.key] !== undefined && sampleValues[p.key] !== null) {
            columns.push({ key: p.key, name: `${p.name}${suffix}, °C`, unit: '°C', type: 'value', category: 'temperatura' });
        }
    });

    // Давление
    const pressureParams = [
        { key: 'P1', name: 'P1' },
        { key: 'P2', name: 'P2' },
        { key: 'P3', name: 'P3' },
        { key: 'P4', name: 'P4' },
    ];
    pressureParams.forEach((p) => {
        if (sampleValues[p.key] !== undefined && sampleValues[p.key] !== null) {
            columns.push({ key: p.key, name: `${p.name}${suffix}, МПа`, unit: 'МПа', type: 'value', category: 'davlenie' });
        }
    });

    // Объёмный расход
    const volumeParams = [
        { key: 'V1', name: 'V1' },
        { key: 'V2', name: 'V2' },
        { key: 'V3', name: 'V3' },
        { key: 'V4', name: 'V4' },
    ];
    volumeParams.forEach((p) => {
        if (sampleValues[p.key] !== undefined && sampleValues[p.key] !== null) {
            columns.push({ key: p.key, name: `${p.name}${suffix}, м³`, unit: 'м³', type: 'value', category: 'obemnui_rasxod' });
        }
    });

    // Массовый расход
    const massParams = [
        { key: 'G1', name: 'G1' },
        { key: 'G2', name: 'G2' },
        { key: 'Gd1', name: 'Gd1' },
    ];
    massParams.forEach((p) => {
        if (sampleValues[p.key] !== undefined && sampleValues[p.key] !== null) {
            columns.push({ key: p.key, name: `${p.name}${suffix}, т`, unit: 'т', type: 'value', category: 'massovui_rasxod' });
        }
    });

    // Энергия
    const energyParams = [
        { key: 'Q1', name: 'Q1' },
        { key: 'Q1min', name: 'Q1min' },
        { key: 'Q1max', name: 'Q1max' },
        { key: 'Q2', name: 'Q2' },
        { key: 'Q2min', name: 'Q2min' },
        { key: 'Q2max', name: 'Q2max' },
        { key: 'Q3', name: 'Q3' },
    ];
    energyParams.forEach((p) => {
        if (sampleValues[p.key] !== undefined && sampleValues[p.key] !== null) {
            columns.push({ key: p.key, name: `${p.name}${suffix}, Гкал`, unit: 'Гкал', type: 'value', category: 'energi' });
        }
    });

    return columns;
};

// ============================================
// Построение колонок для интегрального архива
// ============================================
const buildIntegralColumns = (sampleValues) => {
    if (!sampleValues) return [];

    const columns = [];
    columns.push({ key: 'datetime', name: 'Дата/Время', type: 'datetime' });

    // ✅ Время: Наработка + Tmin, Tmax, Tdt, Tf, Tep (без суффикса)
    const timeParams = [
        { key: 'Narabotka', name: 'Наработка' },
        { key: 'Tmin', name: 'Tmin' },
        { key: 'Tmax', name: 'Tmax' },
        { key: 'Tdt', name: 'Tdt' },
        { key: 'Tf', name: 'Tf' },
        { key: 'Tep', name: 'Tep' },
    ];
    timeParams.forEach((p) => {
        if (sampleValues[p.key] !== undefined && sampleValues[p.key] !== null) {
            columns.push({ key: p.key, name: `${p.name}, час`, unit: 'час', type: 'value', category: 'time' });
        }
    });

    // Объём (накопленный)
    const volumeParams = [
        { key: 'V1', name: 'V1' },
        { key: 'V2', name: 'V2' },
        { key: 'V3', name: 'V3' },
        { key: 'V4', name: 'V4' },
    ];
    volumeParams.forEach((p) => {
        if (sampleValues[p.key] !== undefined && sampleValues[p.key] !== null) {
            columns.push({ key: p.key, name: `${p.name}, м³`, unit: 'м³', type: 'value', category: 'obemnui_rasxod' });
        }
    });

    // Масса (накопленная)
    const massParams = [
        { key: 'G1', name: 'G1' },
        { key: 'G2', name: 'G2' },
        { key: 'Gd1', name: 'Gd1' },
    ];
    massParams.forEach((p) => {
        if (sampleValues[p.key] !== undefined && sampleValues[p.key] !== null) {
            columns.push({ key: p.key, name: `${p.name}, т`, unit: 'т', type: 'value', category: 'massovui_rasxod' });
        }
    });

    // Энергия (накопленная)
    const energyParams = [
        { key: 'Q1', name: 'Q1' },
        { key: 'Q1min', name: 'Q1min' },
        { key: 'Q1max', name: 'Q1max' },
        { key: 'Q2', name: 'Q2' },
        { key: 'Q2min', name: 'Q2min' },
        { key: 'Q2max', name: 'Q2max' },
        { key: 'Q3', name: 'Q3' },
    ];
    energyParams.forEach((p) => {
        if (sampleValues[p.key] !== undefined && sampleValues[p.key] !== null) {
            columns.push({ key: p.key, name: `${p.name}, Гкал`, unit: 'Гкал', type: 'value', category: 'energi' });
        }
    });

    return columns;
};

// ============================================
// Получение значения ячейки
// ============================================
const getCellValueK307 = (record, col) => {
    if (col.type === 'datetime') {
        return formatDateTimeK307(record);
    }
    // Данные лежат в record.parameters.values
    const values = record.parameters?.values || record;
    const val = values[col.key];
    if (val === null || val === undefined) return null;
    const num = parseFloat(val);
    if (isNaN(num)) return null;
    if (num === NS_VALUE_K307) return NS_VALUE_K307;
    return num;
};

// ============================================
// Получение накопленного значения из интегральной записи
// Для Карат-307 ключи совпадают (V1→V1, G1→G1, Q1→Q1)
// ============================================
const getAccumulatedValueK307 = (accumData, colKey, _podSystemIndex) => {
    if (!accumData) return null;
    const values = accumData.parameters?.values || accumData;

    if (values[colKey] !== undefined) {
        const num = parseFloat(values[colKey]);
        return isNaN(num) ? null : num;
    }

    return null;
};

// ============================================
// Построение колонок для второй (накопленной) таблицы
// Без температуры и давления, С датой
// Для Карат-307 ключи не меняются (V1 остаётся V1)
// ============================================
const buildAccumColumnsK307 = (columns) => {
    return columns
        .filter(col => {
            if (col.category === 'temperatura' || col.category === 'davlenie') return false;
            return true;
        })
        .map(col => {
            // Для интегральной таблицы меняем единицы: м³/ч→м³, т/ч→т, Гкал/ч→Гкал
            // Но т.к. в колонках уже стоят м³, т, Гкал — просто оставляем как есть
            return col;
        });
};

// ============================================
// Форматирование значения
// ============================================
const formatValueK307 = (val, decimals = 3) => {
    if (val === null || val === undefined || val === '') return '—';
    const num = parseFloat(val);
    if (isNaN(num)) return '—';
    if (num === NS_VALUE_K307) return 'НС';
    return num.toLocaleString('ru-RU', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
    });
};

// ============================================
// Парсинг даты Карат-307 в timestamp (для сортировки)
// ============================================
const parseDateK307 = (record) => {
    const date = record.date || '';
    const time = record.time || '';
    if (!date) return 0;

    if (date.includes('.')) {
        // Формат DD.MM.YYYY
        const parts = date.split('.');
        const timeParts = time ? time.split(':') : ['0', '0', '0'];
        return new Date(
            parseInt(parts[2]),
            parseInt(parts[1]) - 1,
            parseInt(parts[0]),
            parseInt(timeParts[0] || 0),
            parseInt(timeParts[1] || 0),
            parseInt(timeParts[2] || 0)
        ).getTime();
    }

    // Формат YYYY-MM-DD
    return new Date(`${date}T${time || '00:00:00'}`).getTime();
};

// ============================================
// Основной экспорт: генерация отчетов для Карат-307
// ============================================
export const generateKarat307Reports = async (devName, params, headers, deviceFromList) => {
    const reports = [];

    let deviceInfo = {};
    try {
        const infoRes = await axios.get(`${API_URL}/api/devices/karat307/${devName}/info`, { headers });
        deviceInfo = infoRes.data;
    } catch (e) {
        deviceInfo = { name: devName, serial_number: '—' };
    }

    // ✅ Карат-307: НЕ корректируем даты (не прибавляем день/час)
    // date = 2017-07-13 time = 23:59:00 — это сутки за 13.07.2017
    const dateFrom = params.dateFrom;
    const dateTo = params.dateTo;

    const archiveRes = await axios.get(`${API_URL}/api/devices/karat307/${devName}/archive`, {
        params: { type: params.archiveType, from: dateFrom, to: dateTo }, headers
    });

    const archiveData = archiveRes.data;
    const rawRecords = archiveData.records || [];

    // ✅ Дедупликация по (date + time) — страховка от дублей API
    const seen = new Set();
    const records = rawRecords.filter(rec => {
        const key = `${rec.date}_${rec.time}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });

    if (records.length === 0) return reports;

    // Строим колонки из ПЕРВОЙ записи
    const sampleValues = records[0]?.parameters?.values || records[0] || {};
    let columns;
    if (params.archiveType === 'integral') {
        columns = buildIntegralColumns(sampleValues);
    } else {
        columns = buildArchiveColumns(sampleValues, params.archiveType);
    }

    // Сортировка по дате
    const sortedRecords = [...records].sort((a, b) => {
        return parseDateK307(a) - parseDateK307(b);
    });

    // Обработка записей
    const processedRecords = sortedRecords.map(rec => {
        const row = {};
        columns.forEach(col => {
            row[col.key] = getCellValueK307(rec, col);
        });
        return row;
    });

    // Подсчёт итогов и средних
    const totals = {};
    const sums = {};
    const counts = {};

    processedRecords.forEach(r => {
        columns.forEach(col => {
            if (col.type === 'datetime') return;
            const val = r[col.key];
            if (val === NS_VALUE_K307 || val == null || isNaN(parseFloat(val))) return;
            const num = parseFloat(val);
            if (!totals[col.key]) totals[col.key] = 0;
            if (!sums[col.key]) sums[col.key] = 0;
            if (!counts[col.key]) counts[col.key] = 0;

            if (col.category === 'temperatura' || col.category === 'davlenie') {
                sums[col.key] += num;
                counts[col.key]++;
            } else {
                totals[col.key] += num;
            }
        });
    });

    const averages = {};
    Object.keys(sums).forEach(key => {
        averages[key] = counts[key] > 0 ? sums[key] / counts[key] : NS_VALUE_K307;
    });

    // Получаем накопленные значения (начало и конец периода)
    let startAccum = null, endAccum = null;
    if (params.archiveType !== 'integral') {
        try {
            const startRes = await axios.get(`${API_URL}/api/devices/karat307/${devName}/archive`, {
                params: { type: 'integral', from: dateFrom, to: dateFrom }, headers
            });
            startAccum = startRes.data?.records?.[0] || null;
        } catch (e) { }

        // ✅ Для Карат-307: dateTo + 1 день для получения накопленных на конец периода
        const dateToPlusOne = getDatePlusOne(params.dateTo).toISOString().split('T')[0];
        try {
            const endRes = await axios.get(`${API_URL}/api/devices/karat307/${devName}/archive`, {
                params: { type: 'integral', from: dateToPlusOne, to: dateToPlusOne }, headers
            });
            endAccum = endRes.data?.records?.[0] || null;
        } catch (e) { }
    }

    reports.push({
        deviceInfo,
        deviceFromList,
        deviceName: devName,
        deviceType: 'karat307',
        podSystemName: null, // ← Карат-307 не имеет подсистем
        podSystemIndex: 0,
        columns,
        records: processedRecords,
        totals,
        averages,
        startAccumulated: startAccum,
        endAccumulated: endAccum,
        archiveType: params.archiveType
    });

    return reports;
};

export {
    buildAccumColumnsK307,
    getAccumulatedValueK307,
    getCellValueK307,
    formatValueK307,
    NS_VALUE_K307
};